import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import DragIcon from 'mdi-react/DragIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import MinusBoxOutlineIcon from 'mdi-react/MinusBoxOutlineIcon';
import LockOutlineIcon from 'mdi-react/LockOutlineIcon';
import HomeOutlineIcon from 'mdi-react/HomeOutlineIcon';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import { partial } from '../../util/translation/translation';
import FormCard from '../../shared/components/custom/FormCard';
import InfoBox from '../../shared/components/custom/InfoBox';
import SquareIconButton from '../../shared/components/custom/SquareIconButton';

// The tabs between Home and More the app has room for, and how many we
// recommend: with more, each name gets little room in the bar
export const MAX_TABS = 4;
const RECOMMENDED_TABS = 3;

/**
 * The Bottom menu tab of App layout, laid out as the More menu's: on the
 * left the app's components not in the bar, dragged in where they should go
 * or added at the end with +; in the middle the bar, Home first and More
 * last, pinned; on the right the preview.
 */
const BottomMenuEditor = ({
  tabs,
  onDragEnd,
  renderIcon,
  isDisabled,
  onAdd,
  onRemove,
  preview,
}) => {
  const p = partial('appLayout');
  const [search, setSearch] = useState('');
  const available = tabs.Col0?.items || [];
  const bar = tabs.Col1?.items || [];
  const full = bar.length >= MAX_TABS;
  const query = search.trim().toLowerCase();
  const shown = query
    ? available.filter((item) =>
        String(item.name).toLowerCase().includes(query)
      )
    : available;

  // While searching, the list shows only some of the components: an index
  // in it is turned back into the component's place among all of them
  const handleDragEnd = (result) => {
    if (result.source.droppableId !== 'Col0') {
      onDragEnd(result);
      return;
    }
    const index = available.indexOf(shown[result.source.index]);
    onDragEnd({ ...result, source: { ...result.source, index } });
  };

  const renderPinned = (icon, name, hint, className = '') => (
    <div className={`more-editor__pinned ${className}`}>
      <LockOutlineIcon
        size={18}
        className="more-editor__lock"
        aria-label={p('memberRowPinned')}
      />
      <span className="more-editor__icon more-editor__icon--member">
        {icon}
      </span>
      <span className="more-editor__text">
        <span className="more-editor__name">{name}</span>
        <span className="more-editor__sub">{hint}</span>
      </span>
      <span className="more-editor__pin">{p('memberRowPinned')}</span>
    </div>
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="more-editor">
        <FormCard className="more-editor__add" title={p('bottomAddTitle')}>
          <div className="more-editor__label-row more-editor__label-row--first">
            <span className="more-editor__label">{p('moreFromApp')}</span>
            <span className="more-editor__count">
              {p('bottomNotInMenu', { count: available.length })}
            </span>
          </div>
          <label htmlFor="bottom-editor-search" className="more-editor__search">
            <MagnifyIcon size={16} />
            <input
              id="bottom-editor-search"
              type="search"
              value={search}
              placeholder={p('searchComponents')}
              aria-label={p('searchComponents')}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          {full && (
            <div className="more-editor__full" role="status">
              <LockOutlineIcon size={16} />
              {p('bottomFull')}
            </div>
          )}
          <Droppable droppableId="Col0">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`more-editor__available${
                  snapshot.isDraggingOver ? ' more-editor__available--over' : ''
                }`}
              >
                {shown.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={`${item.id}Col0`}
                    index={index}
                    // Full: nothing more can go in, so nothing can be picked up
                    isDragDisabled={full || isDisabled(item)}
                  >
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        className={`more-editor__option${
                          dragSnapshot.isDragging
                            ? ' more-editor__option--dragging'
                            : ''
                        }${
                          isDisabled(item)
                            ? ' more-editor__option--disabled'
                            : ''
                        }${full ? ' more-editor__option--full' : ''}`}
                        title={full ? p('bottomFull') : undefined}
                        style={dragProvided.draggableProps.style}
                      >
                        <DragIcon size={18} className="more-editor__grip" />
                        <span className="more-editor__icon">
                          {renderIcon(item)}
                        </span>
                        <span className="more-editor__name">{item.name}</span>
                        <SquareIconButton
                          icon={<PlusIcon />}
                          title={
                            full
                              ? p('bottomFull')
                              : p('bottomAddItem', { name: item.name })
                          }
                          onClick={() => onAdd(item)}
                          disabled={full || isDisabled(item)}
                        />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {available.length === 0 && (
                  <p className="more-editor__empty">{p('bottomAllUsed')}</p>
                )}
                {available.length > 0 && shown.length === 0 && (
                  <p className="more-editor__empty">{p('noComponentsMatch')}</p>
                )}
              </div>
            )}
          </Droppable>
        </FormCard>

        <FormCard
          className="more-editor__menu"
          bodyClassName="more-editor__menu-body"
          title={p('bottomMenu')}
          actions={
            <span className="more-editor__heading-hint">
              <span
                className={`more-editor__tally${
                  full ? ' more-editor__tally--full' : ''
                }`}
              >
                {p('bottomCount', { count: bar.length, max: MAX_TABS })}
                {full && ` · ${p('bottomFullTag')}`}
              </span>{' '}
              {p('dragToReorder')}
            </span>
          }
        >
          {renderPinned(
            <HomeOutlineIcon size={18} />,
            p('previewHome'),
            p('bottomHomeHint')
          )}
          <Droppable droppableId="Col1">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`more-editor__list${
                  snapshot.isDraggingOver ? ' more-editor__list--over' : ''
                }`}
              >
                {bar.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={`${item.id}Col1`}
                    index={index}
                  >
                    {(dragProvided, dragSnapshot) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                        className={`more-editor__row${
                          dragSnapshot.isDragging
                            ? ' more-editor__row--dragging'
                            : ''
                        }${
                          isDisabled(item) ? ' more-editor__row--disabled' : ''
                        }`}
                        style={dragProvided.draggableProps.style}
                      >
                        <DragIcon size={18} className="more-editor__grip" />
                        <span className="more-editor__icon more-editor__icon--menu">
                          {renderIcon(item)}
                        </span>
                        <span className="more-editor__name">{item.name}</span>
                        <div className="icon-actions more-item__actions">
                          <SquareIconButton
                            icon={<MinusBoxOutlineIcon />}
                            title={p('bottomRemoveItem')}
                            onClick={() => onRemove(item)}
                          />
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {bar.length === 0 && (
                  <p className="more-editor__empty more-editor__empty--menu">
                    {p('bottomEmpty', { max: MAX_TABS })}
                  </p>
                )}
              </div>
            )}
          </Droppable>
          {renderPinned(
            <DotsHorizontalIcon size={18} />,
            p('previewMore'),
            p('bottomMoreHint'),
            'more-editor__pinned--last'
          )}
          <InfoBox
            className={`more-editor__info${
              bar.length > RECOMMENDED_TABS ? ' more-editor__info--warn' : ''
            }`}
          >
            <strong>
              {p('bottomRecommendTitle', { count: RECOMMENDED_TABS })}
            </strong>{' '}
            {p('bottomRecommendText')}
          </InfoBox>
        </FormCard>

        <div className="more-editor__preview">{preview}</div>
      </div>
    </DragDropContext>
  );
};

BottomMenuEditor.propTypes = {
  tabs: PropTypes.shape({
    Col0: PropTypes.shape({ items: PropTypes.arrayOf(PropTypes.object) }),
    Col1: PropTypes.shape({ items: PropTypes.arrayOf(PropTypes.object) }),
  }).isRequired,
  onDragEnd: PropTypes.func.isRequired,
  renderIcon: PropTypes.func.isRequired,
  isDisabled: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  preview: PropTypes.node,
};

BottomMenuEditor.defaultProps = {
  preview: null,
};

export default BottomMenuEditor;
